export const RIDESHARE_REPORT_TYPES = {
  COST_PER_RIDE_TREND: 'cost-per-ride-trend',
  CONTRACTOR_RIDE_COMPLETION_RATE: 'contractor-ride-completion-rate',
  CONTRACTOR_COST_COMPARISON: 'contractor-cost-comparison',
  RIDE_TYPE_COMPARISON: 'ride-type-comparison',
  TOP_5_INCOMPLETE_DRIVER_NO_SHOW_CITIES:
    'top-5-incomplete-driver-no-show-cities',
  RIDES_BY_LEVEL_OF_SERVICE: 'rides-by-level-of-service',
  AVERAGE_TIME_TO_ARRIVAL: 'contractor-average-time-to-arrival',
  MONTHLY_COST_PER_COMPANY: 'contractor-cost-per-company',
  MONTHLY_COUNT_PER_COMPANY: 'contractor-count-per-company',
  MONTHLY_ON_TIME_PERFORMANCE: 'contractor-on-time-performance',
  MONTHLY_COST_PER_LEVEL_OF_SERVICE:
    'contractor-cost-per-level-of-service',
  MONTHLY_COUNT_PER_LEVEL_OF_SERVICE:
    'contractor-count-per-level-of-service',
  MONTHLY_COST_PER_COUNTY: 'contractor-cost-per-county',
  MONTHLY_COUNT_PER_COUNTY: 'contractor-count-per-county',
  MONTHLY_RIDES_BY_STATUS: 'contractor-rides-by-status',
} as const;

export const CONTRACTOR_DASHBOARD_CHART_DESCRIPTIONS = {
  COST_PER_RIDE_TREND:
    'Average cost per ride segment for rides that incurred a cost during the selected period, helping you identify cost trends and changes in transportation spending over time.',
  RIDE_COMPLETION_RATE:
    'Percentage of ride segments completed during the selected period, helping you evaluate overall ride fulfillment performance and service reliability.',
  COST_COMPARISON:
    'Comparison of costs between completed and canceled ride segments during the selected period, helping you understand the financial impact of completed versus unsuccessful ride outcomes.',
  AVERAGE_TIME_TO_ARRIVAL:
    'Average number of minutes drivers arrived after the scheduled pickup time for all rides that have a pickup arrive time during the selected period, helping you monitor contractor timeliness and identify potential service delays.',
  RIDE_TYPE_COMPARISON:
    'Distribution of completed ride segments by ride type (Scheduled & Will Call) during the selected period, helping you understand ride demand patterns and utilization across ride types.',
  TOP_5_INCOMPLETE_DRIVER_NO_SHOW_CITIES:
    'Displays the top 5 cities with the highest count of rides marked as incomplete - driver no show during the selected date range, allowing you to identify gaps or failed ride fulfillments that may require follow-up in specific cities.',
  RIDES_BY_LEVEL_OF_SERVICE:
    'Distribution of completed ride segments by Level of Service during the selected period, helping you understand utilization and demand across transportation service levels.',
} as const;

export const CONTRACTOR_MONTHLY_REPORT_DESCRIPTIONS = {
  COST_PER_COMPANY:
    'Shows the total cost of rides that incurred a cost, for the selected contractor, each month, helping you evaluate cost trends and overall financial performance over time.',
  COUNT_PER_COMPANY:
    'Shows the number of completed rides, for the selected contractor, each month, helping you track ride volume and utilization trends over time.',
  ON_TIME_PERFORMANCE:
    'Displays the monthly on-time performance rate for rides with at least a pickup arrive time, for the selected contractor, calculated by comparing on-time rides to late rides, giving a clear view of service reliability trends.',
  COST_PER_LEVEL_OF_SERVICE:
    'Shows the total cost of rides that incurred a cost, by level of service, for the selected contractor each month, helping you understand spending patterns across transportation service levels.',
  COUNT_PER_LEVEL_OF_SERVICE:
    'Shows the number of completed rides, by level of service, for the selected contractor each month, helping you identify utilization trends across transportation service levels.',
  COST_PER_COUNTY:
    'Shows the total cost of rides that incurred a cost, by pickup county, for the selected contractor, each month, helping you compare transportation spending across service areas.',
  COUNT_PER_COUNTY:
    'Shows the number of completed rides, by pickup county, for the selected contractor each month, helping you identify geographic demand and utilization patterns.',
  RIDES_BY_STATUS:
    'Breaks down rides by status each month (e.g. completed, canceled, incomplete), for the selected contractor, helping track performance trends over time.',
} as const;

// Backward-compatible alias for table modules that still use `RIDE_REPORT_TYPES`.
export const RIDE_REPORT_TYPES = RIDESHARE_REPORT_TYPES;

export type ReportType =
  (typeof RIDESHARE_REPORT_TYPES)[keyof typeof RIDESHARE_REPORT_TYPES];

export const contractorDashboardDetailRoutes = [
  RIDESHARE_REPORT_TYPES.COST_PER_RIDE_TREND,
  RIDESHARE_REPORT_TYPES.CONTRACTOR_RIDE_COMPLETION_RATE,
  RIDESHARE_REPORT_TYPES.CONTRACTOR_COST_COMPARISON,
  RIDESHARE_REPORT_TYPES.TOP_5_INCOMPLETE_DRIVER_NO_SHOW_CITIES,
  RIDESHARE_REPORT_TYPES.RIDE_TYPE_COMPARISON,
  RIDESHARE_REPORT_TYPES.RIDES_BY_LEVEL_OF_SERVICE,
  RIDESHARE_REPORT_TYPES.AVERAGE_TIME_TO_ARRIVAL,
];

export const contractorMonthlyReportRoutes = [
  RIDESHARE_REPORT_TYPES.MONTHLY_COST_PER_COMPANY,
  RIDESHARE_REPORT_TYPES.MONTHLY_COUNT_PER_COMPANY,
  RIDESHARE_REPORT_TYPES.MONTHLY_ON_TIME_PERFORMANCE,
  RIDESHARE_REPORT_TYPES.MONTHLY_COST_PER_LEVEL_OF_SERVICE,
  RIDESHARE_REPORT_TYPES.MONTHLY_COUNT_PER_LEVEL_OF_SERVICE,
  RIDESHARE_REPORT_TYPES.MONTHLY_COST_PER_COUNTY,
  RIDESHARE_REPORT_TYPES.MONTHLY_COUNT_PER_COUNTY,
  RIDESHARE_REPORT_TYPES.MONTHLY_RIDES_BY_STATUS,
] as const;

export const rideshareHideDashboardRoutes = [
  ...contractorDashboardDetailRoutes,
  ...contractorMonthlyReportRoutes,
];

export const tableType = {
  // custom: [RIDESHARE_REPORT_TYPES.TOP_CONTRACTOR_COST],
  custom: [RIDESHARE_REPORT_TYPES.COST_PER_RIDE_TREND],
  monthly: contractorMonthlyReportRoutes,
};

export const RIDESHARE_MEMBER_COLUMNS = [
  {
    key: 'memberName',
    label: 'Member Name',
    type: 'text',
    show: true,
  },
  {
    key: 'memberIdNumber',
    label: 'MemberId',
    type: 'text',
    show: true,
  },
  {
    key: 'memberStatus',
    label: 'Member Status',
    type: 'status',
    show: true,
  },
  {
    key: 'firstDateOfService',
    label: 'First Call Date',
    type: 'date',
    show: true,
  },
  {
    key: 'lastDateOfService',
    label: 'Last Call Date',
    type: 'date',
    show: true,
  },
  {
    key: 'memberLob',
    label: 'Member LOB',
    type: 'text',
    show: true,
  },
  {
    key: 'countRideshareTrips',
    label: 'Count Rideshare Trips',
    type: 'number',
    show: true,
  },
  {
    key: 'totalRideshareCost',
    label: 'Total Rideshare Cost',
    type: 'text',
    show: true,
  },
];

export const RIDESHARE_CONTRACTOR_COST_TREND_COLUMNS = [
  {
    key: 'contractorName',
    label: 'Contractor',
    type: 'text',
    show: true,
  },
  {
    key: 'contractorStatus',
    label: 'Status',
    type: 'status',
    show: true,
  },
  {
    key: 'firstDateOfService',
    label: 'First Date of Service',
    type: 'date',
    show: true,
  },
  {
    key: 'lastDateOfService',
    label: 'Last Date of Service',
    type: 'date',
    show: true,
  },
  {
    key: 'atmSoftware',
    label: 'ATMS Software',
    type: 'text',
    show: true,
  },
  {
    key: 'countOfRides',
    label: 'Count of Rides',
    type: 'number',
    show: true,
  },
  {
    key: 'totalRideCost',
    label: 'Total Ride Cost',
    type: 'text',
    show: true,
  },
  {
    key: 'costPerRide',
    label: 'Cost per Ride',
    type: 'text',
    show: true,
  },
];

export const RIDESHARE_CONTRACTOR_RIDE_COMPLETION_RATE_COLUMNS = [
  {
    key: 'contractorName',
    label: 'Contractor',
    type: 'text',
    show: true,
  },
  {
    key: 'contractorStatus',
    label: 'Status',
    type: 'status',
    show: true,
  },
  {
    key: 'firstDateOfService',
    label: 'First Date of Service',
    type: 'date',
    show: true,
  },
  {
    key: 'lastDateOfService',
    label: 'Last Date of Service',
    type: 'date',
    show: true,
  },
  {
    key: 'atmSoftware',
    label: 'ATMS Software',
    type: 'text',
    show: true,
  },
  {
    key: 'totalRideCount',
    label: 'Total Ride Count',
    type: 'number',
    show: true,
  },
  {
    key: 'totalCompletedRideCount',
    label: 'Total Completed Ride Count',
    type: 'number',
    show: true,
  },
  {
    key: 'rideCompletionRate',
    label: 'Ride Completion Rate',
    type: 'percent',
    show: true,
  },
];

export const RIDESHARE_RIDE_TYPE_COMPARISON_COLUMNS = [
  {
    key: 'contractorName',
    label: 'Contractor',
    type: 'text',
    show: true,
  },
  {
    key: 'contractorStatus',
    label: 'Status',
    type: 'status',
    show: true,
  },
  {
    key: 'firstDateOfService',
    label: 'First Date of Service',
    type: 'date',
    show: true,
  },
  {
    key: 'lastDateOfService',
    label: 'Last Date of Service',
    type: 'date',
    show: true,
  },
  {
    key: 'countOfScheduledRides',
    label: 'Count of Scheduled Rides',
    type: 'number',
    show: true,
  },
  {
    key: 'percentageScheduledType',
    label: 'Percentage Schedule Type',
    type: 'percent',
    show: true,
  },
  {
    key: 'countWillCallRides',
    label: 'Count Will Call Rides',
    type: 'number',
    show: true,
  },
  {
    key: 'percentageWillCallType',
    label: 'Percentage Will Call Type',
    type: 'percent',
    show: true,
  },
];

export const RIDESHARE_TOP_5_NO_SHOW_CITIES_COLUMNS = [
  {
    key: 'contractorName',
    label: 'Contractor',
    type: 'text',
    show: true,
  },
  {
    key: 'contractorStatus',
    label: 'Status',
    type: 'status',
    show: true,
  },
  {
    key: 'firstDateOfService',
    label: 'First Date of Service',
    type: 'date',
    show: true,
  },
  {
    key: 'lastDateOfService',
    label: 'Last Date of Service',
    type: 'date',
    show: true,
  },
  {
    key: 'pickUpAddressCity',
    label: 'City',
    type: 'text',
    show: true,
  },
  {
    key: 'countOfRides',
    label: 'No Show Count',
    type: 'number',
    show: true,
  },
];

export const RIDESHARE_RIDES_BY_LOS_COLUMNS = [
  {
    key: 'contractorName',
    label: 'Contractor',
    type: 'text',
    show: true,
  },
  {
    key: 'contractorStatus',
    label: 'Contractor Status',
    type: 'status',
    show: true,
  },
  {
    key: 'firstDateOfService',
    label: 'First Date of Service',
    type: 'date',
    show: true,
  },
  {
    key: 'lastDateOfService',
    label: 'Last Date of Service',
    type: 'date',
    show: true,
  },
  {
    key: 'los',
    label: 'Level of Service',
    type: 'text',
    show: true,
  },
  {
    key: 'totalRideCount',
    label: 'Total Ride Count',
    type: 'number',
    show: true,
  },
];

export const RIDESHARE_AVERAGE_TIME_TO_ARRIVAL_COLUMNS = [
  {
    key: 'contractorName',
    label: 'Contractor',
    type: 'text',
    show: true,
  },
  {
    key: 'contractorStatus',
    label: 'Status',
    type: 'status',
    show: true,
  },
  {
    key: 'firstDateOfService',
    label: 'First Date of Service',
    type: 'date',
    show: true,
  },
  {
    key: 'lastDateOfService',
    label: 'Last Date of Service',
    type: 'date',
    show: true,
  },
  {
    key: 'totalAssignedRides',
    label: 'Total Assigned Rides',
    type: 'number',
    show: true,
  },
  {
    key: 'averageArrivalTime',
    label: 'Average Arrival Time',
    type: 'number',
    show: true,
  },
];

export const RIDESHARE_COST_COMPARISON_COLUMNS = [
  {
    key: 'contractorName',
    label: 'Contractor',
    type: 'text',
    show: true,
  },
  {
    key: 'contractorStatus',
    label: 'Status',
    type: 'status',
    show: true,
  },

  {
    key: 'firstDateOfService',
    label: 'First Date of Service',
    type: 'date',
    show: true,
  },
  {
    key: 'lastDateOfService',
    label: 'Last Date of Service',
    type: 'date',
    show: true,
  },
  {
    key: 'totalCompletedRideCost',
    label: 'Completed Ride Cost',
    type: 'text',
    show: true,
  },
  {
    key: 'totalCancelledRideCost',
    label: 'Cancelled Ride Cost',
    type: 'text',
    show: true,
  },
];

export const RIDESHARE_SEGMENT_COLUMNS = [
  {
    key: 'contractor',
    label: 'Contractor',
    type: 'text',
    show: true,
  },
  {
    key: 'contractorStatus',
    label: 'Contractor Status',
    type: 'status',
    show: true,
  },
  {
    key: 'fullName',
    label: 'Member Name',
    type: 'text',
    show: true,
  },
  {
    key: 'memberIdNumber',
    label: 'Member ID',
    type: 'text',
    show: true,
  },
  {
    key: 'lob',
    label: 'Member LOB',
    type: 'text',
    show: true,
  },
  {
    key: 'dateOfService',
    label: 'Date of Service',
    type: 'date',
    show: true,
  },
  {
    key: 'confirmationCode',
    label: 'Confirmation Code',
    type: 'text',
    show: true,
  },
  {
    key: 'levelOfService',
    label: 'Level of Service',
    type: 'text',
    show: true,
  },
  {
    key: 'rideStatus',
    label: 'Ride Status',
    type: 'status',
    show: true,
  },
  {
    key: 'statusGroup',
    label: 'Status Group',
    type: 'status',
    show: true,
  },
  {
    key: 'serviceType',
    label: 'Rendering Service',
    type: 'text',
    show: true,
  },
  {
    key: 'pickupAddress',
    label: 'Pickup Address',
    type: 'text',
    show: true,
  },
  {
    key: 'dropOffAddress',
    label: 'Drop-off Address',
    type: 'text',
    show: true,
  },
  {
    key: 'scheduledPickupTime',
    label: 'Pickup Time',
    type: 'time',
    show: false,
    sortingField: 'pickupTime',
  },
  {
    key: 'cost',
    label: 'Total Ride Cost',
    type: 'text',
    show: true,
  },
  {
    key: 'estMiles',
    label: 'Miles',
    type: 'number',
    show: false,
  },
  {
    key: 'serviceTypeCategory',
    label: 'Rendering Service Category',
    type: 'text',
    show: false,
  },
  {
    key: 'dropOffAddressCounty',
    label: 'Drop-Off County',
    type: 'text',
    show: false,
  },
  {
    key: 'willCallIndicator',
    label: 'Will Call Indicator',
    type: 'text',
    show: false,
  },
  {
    key: 'actualMiles',
    label: 'Actual Miles',
    type: 'text',
    show: false,
  },
  {
    key: 'actualPickupTime',
    label: 'Actual Pickup Time',
    type: 'text',
    show: false,
  },
  {
    key: 'onTimeStatus',
    label: 'On Time Status',
    type: 'status',
    show: false,
  },
];

export const RIDESHARE_COST_COMPARISON_SEGMENT_COLUMNS = [
  {
    key: 'segmentId',
    label: 'Segment ID',
    type: 'number',
    show: true,
  },
  {
    key: 'rideDate',
    label: 'Ride Date',
    type: 'date',
    show: true,
  },
  {
    key: 'scheduledPickupTime',
    label: 'Scheduled Pickup Time',
    type: 'time',
    show: true,
  },
  {
    key: 'confirmationCode',
    label: 'Confirmation Code',
    type: 'text',
    show: true,
  },
  {
    key: 'member',
    label: 'Member',
    type: 'text',
    show: true,
  },
];

export type TableMonthlyType = (typeof tableType.monthly)[number];

export const toRideshareNumberArray = (
  value: unknown
): number[] | undefined => {
  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item));
    return normalized.length ? normalized : undefined;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return [value];
  }

  if (typeof value === 'string') {
    const normalized = value
      .split(',')
      .map((item) => Number(item.trim()))
      .filter((item) => Number.isFinite(item));
    return normalized.length ? normalized : undefined;
  }

  return undefined;
};

export const extractRideshareSegmentIds = (row: Record<string, unknown>) => {
  const direct =
    toRideshareNumberArray(row?.segmentId) ??
    toRideshareNumberArray(row?.segmentIds) ??
    toRideshareNumberArray(row?.rideSegmentIds) ??
    toRideshareNumberArray(row?.rideSegmentId) ??
    toRideshareNumberArray(row?.segmentIDList) ??
    toRideshareNumberArray(row?.segmentIdList);

  if (direct?.length) return direct;

  if (Array.isArray(row?.segments)) {
    const fromSegments = row.segments
      .map((segment) => {
        if (segment == null || typeof segment !== 'object') return NaN;

        return Number(
          (segment as Record<string, unknown>).segmentId ??
            (segment as Record<string, unknown>).rideSegmentId ??
            (segment as Record<string, unknown>).id
        );
      })
      .filter((id) => Number.isFinite(id));

    return fromSegments.length ? fromSegments : undefined;
  }

  return undefined;
};

export const extractRideshareMemberIds = (row: Record<string, unknown>) => {
  return (
    toRideshareNumberArray(row?.memberIds) ??
    toRideshareNumberArray(row?.memberId) ??
    toRideshareNumberArray(row?.memberRecordId)
  );
};

export const resolveRideshareRideSegmentId = (row: Record<string, unknown>) => {
  const fromList = extractRideshareSegmentIds(row)?.[0];
  if (fromList != null) return fromList;

  const rideSegmentId = row?.rideSegmentId;
  if (typeof rideSegmentId === 'number' && Number.isFinite(rideSegmentId)) {
    return rideSegmentId;
  }

  const segmentId = row?.segmentId;
  if (typeof segmentId === 'number' && Number.isFinite(segmentId)) {
    return segmentId;
  }

  return undefined;
};

export const mapRideshareTableRow = (
  row: Record<string, unknown>,
  mode: 'aggregate' | 'segment'
) => {
  const normalizedSegmentIds = extractRideshareSegmentIds(row);
  const normalizedMemberIds = extractRideshareMemberIds(row);

  const withWillCall = Object.prototype.hasOwnProperty.call(
    row,
    'willCallIndicator'
  )
    ? {
        ...row,
        willCallIndicator: row.willCallIndicator ? 'true' : 'false',
      }
    : row;

  if (mode === 'segment') {
    const rideSegmentId = resolveRideshareRideSegmentId(row);

    return {
      ...withWillCall,
      ...(rideSegmentId != null ? { rideSegmentId } : {}),
      ...(rideSegmentId != null ? { segmentId: rideSegmentId } : {}),
      ...(row?.rideId != null ? { rideId: row.rideId } : {}),
      ...(row?.dateOfService != null ? { rideDate: row.dateOfService } : {}),
      ...(row?.pickupTime != null
        ? { scheduledPickupTime: row.pickupTime }
        : {}),
      ...(row?.fullName != null ? { member: row.fullName } : {}),
    };
  }

  return {
    ...withWillCall,
    ...(normalizedSegmentIds ? { segmentId: normalizedSegmentIds } : {}),
    ...(normalizedMemberIds ? { memberIds: normalizedMemberIds } : {}),
  };
};
